import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, DataTable, Pagination, SearchInput, DonutChart, chart } from '../../campaign-os';
import { campaignService, getErrorMessage } from '../../../../services/apiService';
import { useApiData } from '../data/useApiData';
import { useShellToast } from '../useShellToast';
import { filterFailureRows, resendSplit, todoSlices, topReasons, fixScope } from '../data/failureView';
import { EmailFixesPanel } from './EmailFixesPanel';

/**
 * A campaign's "Failed to deliver" tab: why its mail did not arrive, for THIS campaign only.
 *
 * GET /campaigns/:id/failure-analysis gives every failed address with its reason, the provider's
 * exact reply, the cleaned address and whether the vendor can still be reached. Shown as:
 *   - a chart of what happened to every address, and the biggest reasons it did not arrive
 *     (the rest behind "show more"); a Mailtrap block counts as sent, never as a failure;
 *   - the "What happened" table — one row per reason with an example and the exact wording,
 *     an Excel of exactly that reason's addresses, and how many of them the next retry covers
 *     (the retry runs inside this campaign; `onRetry` opens the drawer's dialog);
 *   - every failed address, filterable; and the email-field fixer for this campaign's vendors.
 */
const num = (n) => Number(n || 0).toLocaleString('en-IN');
const PAGE_SIZE = 10;
const CORAL = '#A35A4E';

/** One colour per result group, used by the donut, the bars and the pills alike. */
const GROUP_COLOR = {
  Delivered: chart.teal,
  Bounced: CORAL,
  'Can retry now': chart.teal,
  'Need a new address': CORAL,
  'Fix to confirm first': chart.bronze,
  'Unsubscribed — never': chart.palette[3],
  'Already got it (shared address)': chart.ramp[0],
  'Not sent': chart.bronze,
  'No real address': CORAL,
  'Skipped (not sent)': '#C6982F',
  'Temporary failure': chart.blueTeal,
  Unsubscribed: chart.palette[3],
  'Still in flight': chart.status.Draft,
};
const colorOf = (group) => GROUP_COLOR[group] ?? chart.status.Draft;

const RECOVERY_SHORT = {
  automatic: 'Address cleaned automatically',
  retry: 'Same address — retry',
  confirm_typo: 'Typo fix to confirm',
  needs_new_address: 'Needs a new address',
  do_not_mail: 'Unsubscribed — do not mail',
  already_reached: 'Already got it (shared address)',
};

export function CampaignAdvanced({ campaignId, canResend, onResent, onRetry }) {
  const toast = useShellToast();
  const a = useApiData(() => campaignService.failureAnalysis(campaignId), [campaignId], { cacheKey: `campaign:failures:${campaignId}` });
  const data = a.data?.data || null;

  const [busy, setBusy] = useState(null); // which Excel is being built: 'all' or a reason key
  const [reason, setReason] = useState('all');
  const [allReasons, setAllReasons] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const listRef = useRef(null);
  useEffect(() => setPage(1), [reason, search]);

  const byReason = useMemo(() => {
    const m = new Map();
    for (const r of data?.rows || []) {
      if (!m.has(r.reason)) m.set(r.reason, []);
      m.get(r.reason).push(r);
    }
    return m;
  }, [data]);

  const rows = useMemo(() => filterFailureRows(data?.rows, { reason, search }), [data, reason, search]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const failedVendorIds = useMemo(() => fixScope(data), [data]);

  const download = async (key) => {
    if (busy) return;
    setBusy(key);
    try {
      const name = await campaignService.downloadFailureAnalysis(campaignId, key === 'all' ? undefined : key);
      toast(`${name} downloaded`, { tone: 'success', icon: 'download' });
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not build the Excel', { tone: 'error', icon: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const showAddresses = (key) => {
    setReason(key);
    setSearch('');
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (a.loading && !data) {
    return (
      <p className="flex items-center gap-2 py-10 text-[13px] text-cos-ink-muted">
        <Icon name="progress_activity" className="animate-spin text-[16px]" />
        Working out why each mail did not arrive — this checks every failed address, so it can take a little while…
      </p>
    );
  }
  if (a.error && !data) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-cos-status-coral/30 bg-cos-status-coral-bg px-3 py-2.5 text-[12px] text-cos-status-coral">
        <span>{a.error}</span>
        <Button size="sm" variant="secondary" icon="refresh" onClick={a.refetch}>Retry</Button>
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals;
  const slices = todoSlices(data);
  // The addresses that did not get it, in two halves: never handed over, and refused by the
  // receiving server (bounced, temporary failures, spam).
  const neverSent = Number(t.never_sent ?? data.reasons.filter((r) => r.result === 'Not sent').reduce((n, r) => n + r.count, 0));
  const bounced = t.failed - neverSent;
  const retryNow = Number(data.recovery.automatic || 0) + Number(data.recovery.retry || 0);
  const toConfirm = Number(data.recovery.confirm_typo || 0);
  const needNew = Number(data.recovery.needs_new_address || 0) + toConfirm;
  const gotElsewhere = Number(t.reached_elsewhere || 0) + Number(data.recovery.already_reached || 0);
  const maxReason = Math.max(...data.reasons.map((r) => r.count), 1);
  const top = topReasons(data.reasons, 4);
  const bars = allReasons ? [...top.shown, ...top.hidden] : top.shown;

  return (
    <div className="space-y-4">
      {/* KPIs + the full Excel */}
      <div className="flex flex-wrap items-stretch gap-3">
        {[
          { label: 'Did not get it', value: t.failed, tone: 'coral', hint: `${num(bounced)} bounced + ${num(neverSent)} never sent` },
          {
            label: 'Can retry now',
            value: retryNow,
            tone: 'teal',
            hint: 'field fixed, or a temporary failure — sent again from this campaign',
            action: canResend && retryNow > 0 && onRetry ? { label: 'Retry', onClick: onRetry } : null,
          },
          { label: 'Need a new address', value: needNew, hint: toConfirm > 0 ? `incl. ${num(toConfirm)} with a fix to confirm below` : 'the vendor must give a working one' },
          { label: 'Unsubscribed — never', value: t.unsubscribed, excel: t.unsubscribed > 0 ? 'unsubscribed' : null },
          ...(gotElsewhere > 0 ? [{ label: 'Already got it', value: gotElsewhere, hint: 'same address under another vendor record — counted as delivered' }] : []),
          ...(t.awaiting > 0 ? [{ label: 'Still in flight', value: t.awaiting, hint: 'waiting for a delivery result' }] : []),
        ].map((k) => (
          <div key={k.label} className="glass-inset min-w-[120px] flex-1 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className={`mono text-[22px] font-bold leading-none ${k.tone === 'coral' ? 'text-cos-status-coral' : k.tone === 'teal' ? 'text-cos-teal-deep' : 'text-cos-ink'}`}>{num(k.value)}</div>
              {k.excel && (
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => download(k.excel)}
                  title={`Excel of the ${num(k.value)} ${k.label.toLowerCase()} addresses`}
                  className="inline-flex items-center gap-0.5 rounded-lg px-1.5 py-0.5 text-[11px] font-semibold text-cos-teal-deep hover:bg-white/70 disabled:opacity-50"
                >
                  <Icon name="download" className="text-[14px]" />
                  {busy === k.excel ? '…' : 'Excel'}
                </button>
              )}
              {k.action && (
                <Button size="sm" variant="primary" icon="refresh" onClick={k.action.onClick}>
                  {k.action.label}
                </Button>
              )}
            </div>
            <div className="mt-1.5 text-2xs font-medium uppercase tracking-wide text-cos-ink-muted">{k.label}</div>
            {k.hint && <div className="mt-0.5 text-[11px] leading-[14px] text-cos-ink-muted">{k.hint}</div>}
          </div>
        ))}
        <div className="flex flex-col justify-center gap-2">
          <Button size="sm" variant="primary" icon="download" disabled={!!busy} onClick={() => download('all')}>
            {busy === 'all' ? 'Building…' : 'Download all (Excel)'}
          </Button>
          <Button size="sm" variant="ghost" icon="refresh" onClick={a.refetch}>Refresh</Button>
        </div>
      </div>

      {t.failed === 0 ? (
        <div className="glass-inset flex items-center gap-3 p-4 text-[13px] text-cos-ink-secondary">
          <Icon name="task_alt" className="text-[22px] text-cos-teal-deep" />
          Nothing failed on this campaign{t.awaiting ? ` — ${num(t.awaiting)} still waiting for a delivery result` : ''}.
        </div>
      ) : (
        <>
          {/* Graphs */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            <div className="glass-inset p-4 lg:col-span-2">
              <div className="mb-1 text-[13px] font-semibold text-cos-ink">What to do with the {num(t.failed)}</div>
              <DonutChart data={slices} colorFor={colorOf} centerValue={num(t.failed)} centerLabel="Did not get it" height={190} />
            </div>
            <div className="glass-inset p-4 lg:col-span-3">
              <div className="mb-3 flex items-baseline justify-between gap-2">
                <div className="text-[13px] font-semibold text-cos-ink">Why it did not arrive</div>
                <span className="text-2xs text-cos-ink-muted">{num(t.failed)} addresses · click a bar to list them</span>
              </div>
              <div className="space-y-2">
                {bars.map((r) => (
                  <button key={r.key} type="button" onClick={() => showAddresses(r.key)} className="group block w-full text-left">
                    <div className="mb-0.5 flex items-baseline justify-between gap-3 text-[12px]">
                      <span className="truncate text-cos-ink group-hover:text-cos-teal-deep">
                        <span className="font-semibold" style={{ color: colorOf(r.result) }}>{r.result}:</span> {r.label}
                      </span>
                      <span className="mono shrink-0 font-bold text-cos-ink">{num(r.count)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-[rgba(70,80,72,0.07)]">
                      <div className="h-full rounded-full" style={{ width: `${(r.count / maxReason) * 100}%`, background: colorOf(r.result) }} />
                    </div>
                  </button>
                ))}
              </div>
              {top.hidden.length > 0 && (
                <button
                  type="button"
                  onClick={() => setAllReasons((v) => !v)}
                  className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-cos-teal-deep hover:underline"
                >
                  <Icon name={allReasons ? 'expand_less' : 'expand_more'} className="text-[16px]" />
                  {allReasons
                    ? 'Show the top 4 only'
                    : `Show ${num(top.hidden.length)} more (${num(top.hidden.reduce((n, r) => n + r.count, 0))} addresses)`}
                </button>
              )}
            </div>
          </div>

          {/* What happened — one row per reason */}
          <div className="glass-inset overflow-hidden">
            <div className="flex items-baseline justify-between gap-3 px-4 pt-4">
              <div>
                <div className="text-[13px] font-semibold text-cos-ink">What happened</div>
                <p className="text-2xs text-cos-ink-muted">Every reason with a real example and the exact reply. Excel = exactly that reason&apos;s addresses with full detail.</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="mt-3 w-full border-collapse text-[12px]">
                <thead>
                  <tr className="border-y border-cos-line text-left text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted">
                    <th className="px-4 py-2">What happened</th>
                    <th className="px-3 py-2 text-right">Addresses</th>
                    <th className="px-3 py-2">Example &amp; exact reply</th>
                    <th className="px-3 py-2">Next retry</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.reasons.map((r) => (
                    <ReasonRow
                      key={r.key}
                      r={r}
                      rows={byReason.get(r.key) || []}
                      busy={busy}
                      onDownload={() => download(r.key)}
                      onShow={() => showAddresses(r.key)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Notes />

          {/* Every failed address */}
          <div ref={listRef} className="glass-inset scroll-mt-4 overflow-hidden">
            <div className="flex flex-col gap-2 px-4 pt-4">
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-[13px] font-semibold text-cos-ink">Every failed address</div>
                <span className="text-2xs text-cos-ink-muted">
                  <span className="mono font-bold text-cos-ink">{num(rows.length)}</span> of {num(data.rows.length)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full sm:w-[320px]">
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    aria-label="Reason"
                    className="h-10 w-full cursor-pointer appearance-none rounded-xl border border-cos-line bg-white/60 pl-3 pr-9 text-sm font-medium text-cos-ink focus:border-cos-teal focus:outline-none"
                  >
                    <option value="all">Reason: All</option>
                    {data.reasons.map((r) => (
                      <option key={r.key} value={r.key}>{`${r.result}: ${r.label} (${num(r.count)})`}</option>
                    ))}
                  </select>
                  <Icon name="expand_more" className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[18px] text-cos-ink-muted" />
                </div>
                <SearchInput value={search} onChange={setSearch} placeholder="Search vendor, code or address…" className="min-w-[200px] flex-1" />
                {reason !== 'all' && (
                  <Button size="sm" variant="secondary" icon="download" disabled={!!busy} onClick={() => download(reason)}>
                    {busy === reason ? 'Building…' : 'Excel of these'}
                  </Button>
                )}
              </div>
            </div>
            <div className="px-1">
              <DataTable columns={COLUMNS} rows={pageRows} rowKey={(r) => r.message_id} emptyLabel="No failed address matches." />
            </div>
            {rows.length > PAGE_SIZE && (
              <Pagination className="border-t border-cos-line-subtle px-4 py-3" page={page} pageSize={PAGE_SIZE} total={rows.length} onPageChange={setPage} noun="addresses" />
            )}
          </div>

          {failedVendorIds.length > 0 && <EmailFixesPanel vendorIds={failedVendorIds} onApplied={a.refetch} />}
        </>
      )}

    </div>
  );
}

export default CampaignAdvanced;

// ---------------------------------------------------------------------------------------------

function ReasonRow({ r, rows, busy, onDownload, onShow }) {
  const [open, setOpen] = useState(false);
  const split = useMemo(() => resendSplit(rows), [rows]);
  const color = colorOf(r.result);

  return (
    <>
      <tr className="border-b border-cos-line-subtle align-top">
        <td className="px-4 py-3">
          <span className="inline-block rounded-full px-2 py-0.5 text-2xs font-semibold text-white" style={{ background: color }}>{r.result}</span>
          <div className="mt-1 max-w-[230px] text-[13px] font-semibold leading-[17px] text-cos-ink">{r.label}</div>
        </td>
        <td className="mono px-3 py-3 text-right text-[15px] font-bold text-cos-ink">{num(r.count)}</td>
        <td className="px-3 py-3">
          {r.example ? (
            <div className="max-w-[340px]">
              <div className="truncate text-cos-ink-secondary" title={r.example.company_name || ''}>
                {r.example.company_name || 'Unregistered recipient'}
                {r.example.vendor_code && <span className="mono text-cos-ink-muted"> · {r.example.vendor_code}</span>}
              </div>
              <div className="mono break-all text-cos-ink">{r.example.email}</div>
              {r.example.exact && <div className="mono mt-1 line-clamp-2 break-words text-[11px] text-cos-ink-muted" title={r.example.exact}>“{r.example.exact}”</div>}
            </div>
          ) : (
            '—'
          )}
        </td>
        <td className="px-3 py-3">
          <div className="max-w-[230px] text-[12px] leading-[16px]">
            {split.vendorIds.length > 0 ? (
              <span className="font-semibold text-cos-teal-deep">
                {num(split.vendorIds.length)} vendor{split.vendorIds.length === 1 ? '' : 's'} in the next retry
              </span>
            ) : (
              <span className="text-cos-ink-muted">none — needs a new address</span>
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="secondary" icon="download" disabled={!!busy} onClick={onDownload} title={`Excel of the ${r.count} addresses`}>
              {busy === r.key ? '…' : 'Excel'}
            </Button>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? 'Hide details' : 'Show details'}
              className="grid h-9 w-9 place-items-center rounded-lg text-cos-ink-muted transition-colors hover:bg-white/70 hover:text-cos-ink"
            >
              <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[20px]" />
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-cos-line-subtle bg-white/40">
          <td colSpan={5} className="px-4 py-3">
            <div className="grid gap-3 text-[12px] leading-[17px] md:grid-cols-2">
              <Detail label="What it means">{r.meaning}</Detail>
              <Detail label="What to do">{r.fix}</Detail>
            </div>
            <Button size="sm" variant="ghost" icon="table_rows" className="mt-2" onClick={onShow}>
              List all {num(r.count)} addresses
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted">{label}</div>
      <div className="mt-0.5 text-cos-ink-secondary">{children}</div>
    </div>
  );
}

function Notes() {
  return (
    <div>
      <div className="glass-inset p-4 text-[12px] leading-[18px] text-cos-ink-secondary">
        <div className="mb-1.5 text-[13px] font-semibold text-cos-ink">Mailbox does not exist vs. domain does not exist</div>
        <p>
          <span className="font-semibold text-cos-ink">Mailbox does not exist</span> — the company&apos;s mail server is real and answered, but has
          nobody by that name (<span className="mono">5.1.1</span> “user unknown”). That one address is wrong or closed, often because the person left.
        </p>
        <p className="mt-1.5">
          <span className="font-semibold text-cos-ink">Domain does not exist</span> — nothing answers for the part after the @ at all (no DNS record):
          a typo such as <span className="mono">.co</span> for <span className="mono">.com</span>, or a company domain that expired. Nobody there can receive mail.
          Both need a new address from the vendor.
        </p>
      </div>
    </div>
  );
}

const COLUMNS = [
  {
    key: 'vendor',
    header: 'Vendor',
    render: (r) => (
      <div className="min-w-[150px] max-w-[220px]">
        <div className="truncate font-medium text-cos-ink" title={r.company_name || ''}>{r.company_name || 'Unregistered recipient'}</div>
        <div className="mono text-2xs text-cos-ink-muted">{r.vendor_code || '—'} · {r.field}</div>
        {r.also_vendor_ids?.length > 0 && (
          <div className="text-2xs text-cos-teal-deep" title={r.also_covers || ''}>
            + {r.also_vendor_ids.length} more vendor{r.also_vendor_ids.length === 1 ? '' : 's'} on this address
          </div>
        )}
      </div>
    ),
  },
  { key: 'recipient', header: 'Address mailed', render: (r) => <div className="mono max-w-[220px] break-all text-[12px] text-cos-ink">{r.recipient}</div> },
  {
    key: 'reason',
    header: 'Reason',
    render: (r) => (
      <div className="min-w-[150px] max-w-[200px] text-[12px] leading-[16px]">
        <span className="font-semibold" style={{ color: colorOf(r.result) }}>{r.result}</span>
        <div className="text-cos-ink">{r.label}</div>
      </div>
    ),
  },
  {
    key: 'exact',
    header: 'Exact reply / problem',
    render: (r) => (
      <div className="max-w-[280px] text-[12px] leading-[16px] text-cos-ink-secondary">
        {r.problems?.length > 0 && <div className="mb-1 font-medium text-cos-ink">{r.problems.join(' · ')}</div>}
        <div className="line-clamp-3 break-words" title={r.exact}>{r.exact || '—'}</div>
      </div>
    ),
  },
  { key: 'clean', header: 'Clean address', render: (r) => <div className="mono max-w-[200px] break-all text-[12px] text-cos-teal-deep">{r.clean || '—'}</div> },
  { key: 'recovery', header: 'Reachable?', render: (r) => <div className="min-w-[120px] text-[12px] leading-[16px] text-cos-ink">{RECOVERY_SHORT[r.recovery] || r.recovery_label}</div> },
];
